import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import { GetCommunicationwayData } from '../../Redux/Actions/CommunicationWay_Action';
import { CommunicationwayActions } from './CommunicationwayActions';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconFilter  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconCopy    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconPlus    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconMsg     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const IconUsers   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconTrend   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;

/* ─── Component ──────────────────────────────────────────────────────────── */
export const CommunicationTable = () => {
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [loading,      setLoading]      = useState(true);
  const [userlogin,    setUserlogin]    = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [showFilter,   setShowFilter]   = useState(false);
  const [copyOpen,     setCopyOpen]     = useState(false);
  const [page,         setPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const run = async () => {
    setLoading(true);
    await dispatch(GetCommunicationwayData());
    setLoading(false);
  };
  useEffect(() => { run(); }, [dispatch]);

  const res = useSelector((state) => state.AllCommunicationway.communicationway);
  const allItems = Array.isArray(res?.data)
    ? res.data.filter((item) => item?.name?.toLowerCase() !== 'no communication way')
    : [];

  /* ── filter ── */
  const filtered = allItems.filter((item) => {
    const q = searchTerm.toLowerCase();
    return !q || item.name?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems  = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /* ── stats ── */
  const newestItem = allItems.length
    ? allItems.reduce((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? a : b)
    : null;

  const homeLink =
    userlogin?.data?.role === 'Admin'      ? '/AllLeadsForAdmin'
  : userlogin?.data?.role === 'Marketer'   ? '/ALLLeadsForMarketer'
  : userlogin?.data?.role === 'Accountant' ? '/LeadsForAccountant'
  : '/Dashboard';

  /* ── page numbers ── */
  const buildPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (page <= 4)                   pages.push(1, 2, 3, 4, 5, '...', totalPages);
    else if (page >= totalPages - 3) pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    else                             pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    return pages;
  };

  /* ─── render ─── */
  return (
    <>
      <div style={{
        position: 'fixed',
        top: '50px', left: sidebarWidth, right: 0, bottom: 0,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        backgroundColor: isDark ? '#0f1117' : '#f0f2f5',
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
      }}>

        {/* ── Breadcrumb ── */}
        <div style={{
          padding: '14px 28px', flexShrink: 0,
          backgroundColor: isDark ? '#1a1d2e' : '#f0f2f5',
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        }}>
          <Link to={homeLink} style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
            Home
          </Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Communication Ways</span>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* ── Stats ── */}
          <section className="ap-stats" style={{ padding: '24px 28px 0', flexShrink: 0 }}>

            <div className="ap-stat-card light">
              <div>
                <div className="ap-stat-label">TOTAL WAYS</div>
                <div className="ap-stat-value">{allItems.length}</div>
                <div className="ap-stat-badge green">↑ Active in system</div>
              </div>
              <div className="ap-stat-icon light-icon"><IconMsg /></div>
            </div>

            <div className="ap-stat-card light">
              <div>
                <div className="ap-stat-label">SHOWING</div>
                <div className="ap-stat-value">{filtered.length}</div>
                <div className="ap-stat-badge green">Filtered results</div>
              </div>
              <div className="ap-stat-icon light-icon"><IconUsers /></div>
            </div>

            <div className="ap-stat-card dark-card">
              <div>
                <div className="ap-stat-label white">LATEST WAY</div>
                <div className="ap-stat-value white" style={{ fontSize: 18, marginTop: 4 }}>
                  {newestItem?.name || '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                  <span className="ap-badge-pill">MOST RECENT</span>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                    {newestItem ? moment(newestItem.createdAt).format('MMM DD, YYYY') : ''}
                  </span>
                </div>
              </div>
              <div className="ap-stat-icon dark-icon"><IconTrend /></div>
            </div>
          </section>

          {/* ── Table Section ── */}
          <section
            className="ap-table-section"
            style={{ margin: '24px 28px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
          >

            {/* header */}
            <div className="ap-table-header" style={{ flexShrink: 0 }}>
              <div>
                <div className="ap-table-title-label">CRM</div>
                <div className="ap-table-title">COMMUNICATION WAYS</div>
              </div>
              <div className="ap-table-actions">
                <div className="ap-search-inline">
                  <IconSearch />
                  <input
                    placeholder="Search by name…"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  />
                </div>
                <Link to="/AddCommunicationway" style={{ textDecoration: 'none' }}>
                  <button className="ap-create-btn"><IconPlus /> Add New</button>
                </Link>
              </div>
            </div>

            {/* ── Table ── */}
            <div className="ap-table-wrap" style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
              <table className="ap-table">
                <thead>
                  <tr>
                    <th style={{ width: 40, position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>
                      <input type="checkbox" className="ap-checkbox" />
                    </th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>NAME</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>CREATED DATE</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="ap-empty"><div className="ap-spinner" /><span>Loading…</span></td></tr>
                  ) : pageItems.length === 0 ? (
                    <tr><td colSpan={4} className="ap-empty"><IconSearch /><p>{searchTerm ? 'No results match your search' : 'No data available'}</p></td></tr>
                  ) : (
                    pageItems.map((item, i) => (
                      <tr key={item._id || i} className="ap-row">
                        <td><input type="checkbox" className="ap-checkbox" /></td>

                        <td>
                          <div className="ap-project-cell">
                            <div className="ap-project-thumb">
                              {item.name?.slice(0, 2).toUpperCase() || 'CW'}
                            </div>
                            <span className="ap-project-name">{item.name || '—'}</span>
                          </div>
                        </td>

                        <td className="ap-cell-muted" style={{ fontSize: 13 }}>
                          {item.createdAt ? moment(item.createdAt).format('MMM DD, YYYY') : '—'}
                        </td>

                        <td>
                          <div className="ap-actions">
                            <CopyToClipboard
                              text={item._id}
                              onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}
                            >
                              <button className="ap-action-btn" title="Copy ID"><IconCopy /></button>
                            </CopyToClipboard>
                            <CommunicationwayActions communication={item} />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            {filtered.length > 0 && (
              <div
                className="ap-pagination"
                style={{
                  flexShrink: 0,
                  backgroundColor: isDark ? '#1a1a2e' : '#ffffff',
                  borderTop: `1px solid ${isDark ? '#2a2a45' : '#e8eaf0'}`,
                  boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
                }}
              >
                <div className="ap-pagination-info">
                  <span>Items per page:</span>
                  <select
                    className="ap-page-select"
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
                  >
                    {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span>
                    Showing <strong>{(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, filtered.length)}</strong>
                    {' '}of <strong>{filtered.length}</strong>
                  </span>
                </div>
                <div className="ap-pagination-btns">
                  <button className="ap-pag-btn" onClick={() => setPage(1)} disabled={page === 1}>⟨|</button>
                  <button className="ap-pag-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                  {buildPageNumbers().map((n, idx) =>
                    n === '...'
                      ? <span key={`e-${idx}`} className="ap-pag-ellipsis">…</span>
                      : <button key={n} className={`ap-pag-btn ${page === n ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                  )}
                  <button className="ap-pag-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                  <button className="ap-pag-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>|⟩</button>
                </div>
              </div>
            )}
          </section>

          {/* Footer */}
          <footer className="ap-footer" style={{ padding: '16px 28px', flexShrink: 0 }}>
            <span>© 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.</span>
          </footer>
        </div>
      </div>

      <Snackbar open={copyOpen} autoHideDuration={2000} onClose={() => setCopyOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success" sx={{ width: '100%' }}>Copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};